import React from 'react';
import moment from 'moment';
import { ApolloError, InteropQueryResult } from '@apollo/client';
import { Box } from '@mui/material';
import {
  LenderAverageRatePointViewModel,
  LenderConversionTableRowViewModel,
  LenderPerformanceReportFilters,
  LenderPerformanceReportInput,
  LenderTermDistributionRowViewModel,
  LenderTermsComparisonRowViewModel,
  LenderTotalCostRowViewModel,
  LenderTrendPointViewModel,
} from '@trnsact/trnsact-shared-types';
import { formatPercentage } from 'utils';
import { MUITableColumn } from 'components/shared/MUITable';
import {
  CONVERSION_TABLE_COLUMNS,
  FUNNEL_MAX_BAR_HEIGHT,
  FUNNEL_MIN_BAR_RATIO,
  LOOK_TO_BOOK_HEATMAP_MAX_COLOR,
  LOOK_TO_BOOK_HEATMAP_MIN_COLOR,
  LOOK_TO_BOOK_HEATMAP_NO_DATA_COLOR,
  TERM_DISTRIBUTION_COLORS,
  TERMS_COMPARISON_TABLE_COLUMNS,
} from '../constants';

import {
  AsyncResource,
  AverageRateByLenderDatum,
  ConversionTableColumnKey,
  ConversionTableRow,
  LenderPerformanceFilterFormValues,
  LookToBookByLenderDatum,
  LookToBookHeatmapDataset,
  LookToBookHeatmapRow,
  MonthlyLookToBookDatum,
  TermDistributionBucketConfig,
  TermDistributionByLenderDatum,
  TermsComparisonColumnKey,
  TermsComparisonRow,
  TotalCostByLenderDatum,
} from '../types';
import { QueryResponse } from '../../../global';
import { LenderPerformanceNamespace } from '../api/types';

export const mapFiltersToReportInput = (values: LenderPerformanceFilterFormValues): LenderPerformanceReportInput => ({
  filters: {
    created: {
      from: values.from.format('YYYY-MM-DD'),
      to: values.to.format('YYYY-MM-DD'),
    },

    lenderProfileIds: values.lenders.map(option => String(option.value)),
    salesRepsIds: values.salesReps.map(option => String(option.value)),
    locationIds: values.locations.map(option => String(option.value)),
  },
});

export const toAsyncResource = <Entity,>(
  response: InteropQueryResult<LenderPerformanceNamespace<QueryResponse<Entity>>, Record<string, unknown>>,
  fallback: Entity
): AsyncResource<Entity> => ({
  loading: response.loading,
  data: response?.data?.lenderPerformance?.result?.entity ?? fallback,
  error: response?.error ?? null,
});

export const getFunnelPercentOfSubmitted = (value: number, applicationReceived: number) =>
  applicationReceived ? formatPercentValue((value / applicationReceived) * 100) : '—';

export const getFunnelBarHeight = (value: number, submitted: number) => {
  const ratio = submitted ? value / submitted : 0;
  return (FUNNEL_MIN_BAR_RATIO + ratio * (1 - FUNNEL_MIN_BAR_RATIO)) * FUNNEL_MAX_BAR_HEIGHT;
};

const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const formatCompactCurrency = (value: number) => compactCurrencyFormatter.format(value);

export const getPerformanceColor = (value: number): string => {
  if (value < 50) return 'error.main';
  if (value < 70) return 'warning.main';
  return 'success.main';
};

export const formatPercentValue = (value: number | null) => (value == null ? '' : `${value.toFixed(1)}%`);

export const formatMonthLabel = (month: string): string => {
  const parsed = moment(month, ['YYYY-MM', 'YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format('MMM') : month;
};

export const mapLookToBookByLenderDataset = (rows: LenderConversionTableRowViewModel[]): LookToBookByLenderDatum[] =>
  [...rows]
    .filter(row => row.lookToBook !== 0 || row.approvalPct !== 0)
    .sort((a, b) => b.lookToBook - a.lookToBook)
    .map(row => ({
      lender: row.lenderName,
      lookToBook: row.lookToBook,
      approvalRate: row.approvalPct,
      approvalGap: Math.max(row.approvalPct - row.lookToBook, 0),
    }));

export const mapMonthlyLookToBookDataset = (rows: LenderTrendPointViewModel[]): MonthlyLookToBookDatum[] =>
  rows.map(row => ({
    month: row.month,
    approvalRate: row.approvalRate,
    fundRate: row.fundRate,
    lookToBook: row.lookToBook,
  }));

// `rows` is typed as `any` because LenderTrendSeriesViewModel / LenderTrendByLenderPointViewModel
// are already present in schema.graphql but not yet published in @trnsact/trnsact-shared-types.
// Shape (per schema): { lenderProfileId, lenderName, points: [{ month, approvalRate, fundRate, lookToBook, ... }] }
const getLenderTrendSeriesKey = (series: any): string => series.lenderProfileId ?? series.lenderName;

const getLenderTrendMaxLookToBook = (series: any): number =>
  (series.points ?? []).reduce((max: number, point: any) => Math.max(max, point.lookToBook ?? 0), 0);

const parseRgbString = (value: string): [number, number, number] => {
  const match = value.match(/\d+/g) ?? ['0', '0', '0'];
  return [Number(match[0]), Number(match[1]), Number(match[2])];
};

const lerp = (min: number, max: number, t: number) => Math.round(min + (max - min) * t);

// Builds a lender x month matrix so every lender is represented (nothing is hidden),
// rows are only ordered by relevance (highest Look-to-Book first) for easier scanning.
export const mapMonthlyLookToBookByLenderHeatmap = (rows: any[]): LookToBookHeatmapDataset => {
  const monthsSet = new Set<string>();
  rows.forEach(series => (series.points ?? []).forEach((point: any) => monthsSet.add(point.month)));
  const months = Array.from(monthsSet).sort((a, b) => a.localeCompare(b));

  const heatmapRows: LookToBookHeatmapRow[] = [...rows]
    .sort((a, b) => getLenderTrendMaxLookToBook(b) - getLenderTrendMaxLookToBook(a))
    .map(series => {
      const valuesByMonth = new Map<string, number>(
        (series.points ?? []).map((point: any) => [point.month, point.lookToBook])
      );

      return {
        key: getLenderTrendSeriesKey(series),
        label: series.lenderName,
        cells: months.map(month => ({ month, value: valuesByMonth.has(month) ? valuesByMonth.get(month)! : null })),
      };
    });

  return { months, rows: heatmapRows };
};

export const getLookToBookHeatmapColor = (value: number | null): string => {
  if (value == null) return LOOK_TO_BOOK_HEATMAP_NO_DATA_COLOR;

  const [minR, minG, minB] = parseRgbString(LOOK_TO_BOOK_HEATMAP_MIN_COLOR);
  const [maxR, maxG, maxB] = parseRgbString(LOOK_TO_BOOK_HEATMAP_MAX_COLOR);
  const t = Math.max(0, Math.min(1, value / 100));

  return `rgb(${lerp(minR, maxR, t)}, ${lerp(minG, maxG, t)}, ${lerp(minB, maxB, t)})`;
};

export const getLookToBookHeatmapTextColor = (value: number | null): string =>
  value != null && value >= 55 ? '#fff' : 'text.primary';

export const mapConversionTableRows = (rows: LenderConversionTableRowViewModel[]): ConversionTableRow[] =>
  rows.map(row => ({
    id: row.lenderProfileId ?? row.lenderName,
    lenderName: row.lenderName,
    sent: row.sent,
    approved: row.approved,
    approvalPct: row.approvalPct,
    funded: row.funded,
    fundPct: row.fundPct,
    lookToBook: row.lookToBook,
    fundedAmount: row.fundedAmount,
  }));

export const exportConversionTableToCsv = (rows: ConversionTableRow[]): void => {
  const headers = CONVERSION_TABLE_COLUMNS.map(col => col.label);

  const csvRows = rows.map(row =>
    CONVERSION_TABLE_COLUMNS.map(col => {
      if (col.key === ConversionTableColumnKey.LenderName) return `"${row.lenderName}"`;
      const value = row[col.key] as number;
      if (col.percent) return value.toFixed(1);
      if (col.currency) return value.toFixed(2);
      return value;
    }).join(',')
  );

  const csv = [headers.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'lender-conversion-table.csv';
  link.click();
  URL.revokeObjectURL(url);
};

export const getConversionTableColumns = (): MUITableColumn<ConversionTableRow>[] =>
  CONVERSION_TABLE_COLUMNS.map(column => ({
    header: column.label,
    sortKey: column.key,
    renderKey: column.key,
    headerCell: { align: column.align },
    cellProps: { align: column.align },
    renderCell: original => {
      if (column.key === ConversionTableColumnKey.LenderName) return original.lenderName;

      const value = original[column.key] as number;

      const content = column.percent
        ? formatPercentage(value.toFixed(1))
        : column.currency
          ? formatCompactCurrency(value)
          : value.toLocaleString();

      return column.colored ? (
        <Box component="span" sx={{ color: getPerformanceColor(value), fontWeight: 500 }}>
          {content}
        </Box>
      ) : (
        content
      );
    },
  }));

export const getRateColor = (value: number): string => {
  if (value < 8) return 'success.main';
  if (value <= 10) return 'warning.main';
  return 'error.main';
};

export const mapTermsComparisonRows = (rows: LenderTermsComparisonRowViewModel[]): TermsComparisonRow[] =>
  rows.map(row => ({
    id: row.lenderProfileId ?? row.lenderName,
    lenderName: row.lenderName,
    fundedAmount: row.fundedAmount,
    avgRate: row.avgRate,
    rateRange: `${formatPercentValue(row.rateRange.min)} – ${formatPercentValue(row.rateRange.max)}`,
    avgTerm: Math.round(row.avgTerm),
    termOptions: row.termOptions.join(' / '),
    docFee: row.docFee,
  }));

export const getTermsComparisonColumns = (): MUITableColumn<TermsComparisonRow>[] =>
  TERMS_COMPARISON_TABLE_COLUMNS.map(column => ({
    header: column.label,
    sortKey: column.sortKey,
    renderKey: column.key,
    headerCell: { align: column.align },
    cellProps: { align: column.align },
    renderCell: original => {
      switch (column.key) {
        case TermsComparisonColumnKey.LenderName:
          return original.lenderName;
        case TermsComparisonColumnKey.RateRange:
          return original.rateRange;
        case TermsComparisonColumnKey.TermOptions:
          return original.termOptions;
        case TermsComparisonColumnKey.AvgTerm:
          return original.avgTerm.toLocaleString();
        case TermsComparisonColumnKey.FundedAmount:
          return formatCompactCurrency(original.fundedAmount);
        case TermsComparisonColumnKey.DocFee:
          return formatCompactCurrency(original.docFee);
        case TermsComparisonColumnKey.AvgRate:
          return (
            <Box component="span" sx={{ color: getRateColor(original.avgRate), fontWeight: 500 }}>
              {formatPercentValue(original.avgRate)}
            </Box>
          );
        default:
          return null;
      }
    },
  }));

export const exportTermsComparisonToCsv = (rows: TermsComparisonRow[]): void => {
  const headers = TERMS_COMPARISON_TABLE_COLUMNS.map(col => col.label);

  const csvRows = rows.map(row =>
    TERMS_COMPARISON_TABLE_COLUMNS.map(col => {
      switch (col.key) {
        case TermsComparisonColumnKey.LenderName:
          return `"${row.lenderName}"`;
        case TermsComparisonColumnKey.FundedAmount:
        case TermsComparisonColumnKey.DocFee:
          return row[col.key].toFixed(2);
        case TermsComparisonColumnKey.AvgRate:
          return row.avgRate.toFixed(2);
        case TermsComparisonColumnKey.RateRange:
          return `"${row.rateRange}"`;
        case TermsComparisonColumnKey.TermOptions:
          return `"${row.termOptions}"`;
        case TermsComparisonColumnKey.AvgTerm:
          return row.avgTerm;
        default:
          return '';
      }
    }).join(',')
  );

  const csv = [headers.join(','), ...csvRows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'lender-terms-comparison.csv';
  link.click();
  URL.revokeObjectURL(url);
};

export const mapAverageRateByLenderDataset = (rows: LenderAverageRatePointViewModel[]): AverageRateByLenderDatum[] =>
  [...rows].sort((a, b) => a.avgRate - b.avgRate).map(row => ({ lender: row.lenderName, avgRate: row.avgRate }));

export const mapTermDistributionDataset = (
  rows: LenderTermDistributionRowViewModel[]
): TermDistributionByLenderDatum[] =>
  rows
    .filter(row => row.buckets.some(bucket => bucket.percentage !== 0))
    .map(row => {
      const datum: TermDistributionByLenderDatum = { lender: row.lenderName };

      row.buckets.forEach(bucket => {
        datum[String(bucket.term)] = bucket.percentage;
      });

      return datum;
    });

export const getTermDistributionBuckets = (
  rows: LenderTermDistributionRowViewModel[]
): TermDistributionBucketConfig[] => {
  const terms = Array.from(new Set(rows.flatMap(row => row.buckets.map(bucket => bucket.term)))).sort((a, b) => a - b);

  return terms.map((term, index) => ({
    term,
    label: `${term} mo`,
    color: TERM_DISTRIBUTION_COLORS[index % TERM_DISTRIBUTION_COLORS.length],
  }));
};

export const mapTotalCostByLenderDataset = (rows: LenderTotalCostRowViewModel[]): TotalCostByLenderDatum[] =>
  [...rows]
    .filter(row => row.docFee !== 0)
    .sort((a, b) => a.docFee - b.docFee)
    .map(row => ({ lender: row.lenderName, docFee: row.docFee }));
